import React, { useEffect, useState } from 'react';
import { ArrowLeft, ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WebPortfolio = () => {
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState(['Tous']);
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/web-projects`),
        axios.get(`${API}/web-categories`)
      ]);

      if (projectsResponse.data?.success) {
        setProjects(projectsResponse.data.data || []);
      }

      if (Array.isArray(categoriesResponse.data)) {
        const names = categoriesResponse.data.map((cat) => cat.name);
        setCategories(['Tous', ...names]);
      }
    } catch (error) {
      console.error('Failed to load web portfolio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = selectedCategory === 'Tous'
    ? projects
    : projects.filter((project) => project.category === selectedCategory);

  const openModal = (project) => {
    setSelectedProject(project);
    setActiveIndex(0);
  };

  const closeModal = () => {
    setSelectedProject(null);
    setActiveIndex(0);
  };

  const goPrev = () => {
    if (!selectedProject) return;
    const total = (selectedProject.screenshots || []).length;
    if (total === 0) return;
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  const goNext = () => {
    if (!selectedProject) return;
    const total = (selectedProject.screenshots || []).length;
    if (total === 0) return;
    setActiveIndex((prev) => (prev + 1) % total);
  };

  useEffect(() => {
    if (!selectedProject) return;
    const handleKey = (event) => {
      if (event.key === 'Escape') closeModal();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedProject]);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      <main className="pt-16">
        <section className="py-20 bg-gradient-to-b from-slate-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Link
                to="/realisations"
                className="inline-flex items-center space-x-2 text-gray-400 hover:text-white mb-8 transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Retour aux réalisations</span>
              </Link>

              <h1 className="text-4xl lg:text-6xl font-bold mb-6 tracking-tight text-white">
                Réalisations web
              </h1>
              <div className="w-16 h-1 rounded-full mx-auto mb-8" style={{ backgroundColor: '#f97316' }}></div>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                Un aperçu de projets web et e-commerce conçus pour convertir et performer.
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'text-white shadow-lg'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                  style={selectedCategory === category ? { backgroundColor: '#f97316' } : {}}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {loading ? (
          <section className="py-20 bg-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#f97316' }}></div>
              <p className="text-gray-400">Chargement des projets...</p>
            </div>
          </section>
        ) : (
          <section className="py-16 bg-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {filteredProjects.length === 0 ? (
                <div className="text-center text-gray-400">
                  Aucun projet pour le moment.
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className="bg-slate-900/70 border border-slate-800 rounded-2xl overflow-hidden cursor-pointer hover:shadow-xl transition-all"
                      onClick={() => openModal(project)}
                    >
                      <div className="relative h-52 bg-slate-950">
                        <img
                          src={project.screenshots?.[0] || 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NTc5Mzc1NDB8MA&ixlib=rb-4.1.0&q=85'}
                          alt={project.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-6">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div>
                            <h3 className="text-xl font-semibold text-white">{project.title}</h3>
                            {project.client && (
                              <p className="text-sm text-gray-400">{project.client}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">{project.date}</span>
                        </div>
                        <p className="text-gray-300 text-sm mb-4">
                          {project.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {(project.technologies || []).map((tech) => (
                            <span
                              key={tech}
                              className="px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 text-gray-200"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-300">
                            {project.category}
                          </span>
                          {project.site_url && (
                            <button
                              className="inline-flex items-center gap-1 text-white hover:text-orange-200 transition-colors"
                              onClick={(event) => {
                                event.stopPropagation();
                                window.open(project.site_url, '_blank');
                              }}
                            >
                              Voir le site
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      <Footer />

      {selectedProject && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl">
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 text-white hover:scale-110 transition-transform z-10"
              style={{ color: 'white' }}
              onMouseEnter={(e) => e.target.style.color = '#f97316'}
              onMouseLeave={(e) => e.target.style.color = 'white'}
            >
              <X className="h-8 w-8" />
            </button>

            <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
              <img
                src={selectedProject.screenshots?.[activeIndex] || 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHx3ZWIlMjBkZXZlbG9wbWVudHxlbnwwfHx8fDE3NTc5Mzc1NDB8MA&ixlib=rb-4.1.0&q=85'}
                alt={selectedProject.title}
                className="w-full max-h-[70vh] object-contain bg-black"
              />

              {(selectedProject.screenshots || []).length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:scale-105 transition-transform"
                    style={{ color: '#f97316' }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={goNext}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 text-white hover:scale-105 transition-transform"
                    style={{ color: '#f97316' }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
            </div>

            <div className="mt-4 text-center">
              <h3 className="text-xl font-bold text-white mb-2">{selectedProject.title}</h3>
              <p className="text-gray-300 text-sm max-w-2xl mx-auto">{selectedProject.description}</p>
              <div className="flex items-center justify-center space-x-4 mt-3">
                <span className="text-gray-400 text-xs">{selectedProject.category}</span>
                <span className="text-gray-400 text-xs">•</span>
                <span className="text-gray-400 text-xs">{selectedProject.date}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPortfolio;
